import React, { useContext, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartPie, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Pie } from 'react-chartjs-2';
import { DataContext } from '../contexts/DataContext';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

const ShipmentsByCarrierPieChart = () => {
    const { shipments, isLoading } = useContext(DataContext);
    const [pieData, setPieData] = useState({
        labels: [],
        datasets: [{
            data: [],
            backgroundColor: [],
            hoverOffset: 4
        }]
    });
    const [carrierTableData, setCarrierTableData] = useState([]);

    useEffect(() => {
        if (!isLoading && shipments && Array.isArray(shipments)) {
            const carrierCounts = shipments.reduce((acc, shipment) => {
                const carrier = shipment.carrierName || 'Unknown';
                acc[carrier] = (acc[carrier] || 0) + 1;
                return acc;
            }, {});

            const sortedCarrierData = Object.entries(carrierCounts)
                .sort((a, b) => b[1] - a[1])
                .reduce((acc, [key, value]) => {
                    acc.labels.push(key);
                    acc.data.push(value);
                    return acc;
                }, { labels: [], data: [] });

            setPieData({
                labels: sortedCarrierData.labels,
                datasets: [{
                    data: sortedCarrierData.data,
                    backgroundColor: sortedCarrierData.labels.map(() => 
                        `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, 0.5)`),
                    hoverOffset: 4
                }]
            });

            setCarrierTableData(sortedCarrierData.labels.map((label, index) => ({
                name: label,
                count: sortedCarrierData.data[index]
            })));
        }
    }, [shipments, isLoading]);

    const options = {
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: true
            },
        },
        maintainAspectRatio: false
    };

    const renderCarrierTable = () => {
        return (
            <div className="mt-4 overflow-y-auto" style={{ maxHeight: "200px" }}>
                <table className="w-full text-sm bg-white shadow rounded-lg overflow-hidden">
                    <thead className="bg-blue-600 text-white">
                        <tr>
                            <th className="px-4 py-2">Carrier</th>
                            <th className="px-4 py-2">Shipments</th>
                        </tr>
                    </thead>
                    <tbody>
                        {carrierTableData.map(({ name, count }) => (
                            <tr key={name} className="border-b">
                                <td className="px-4 py-2">{name}</td>
                                <td className="px-4 py-2">{count}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="bg-white shadow rounded-lg p-4 w-full max-w-lg flex flex-col justify-between h-custom-card-big overflow-y-auto">
            <div className="mb-4">
                <h2 className="text-lg font-bold text-gray-800 flex items-center">
                    <FontAwesomeIcon icon={faChartPie} className="text-blue-600 mr-2" />
                    Shipments by Carrier
                </h2>
            </div>
            <div className="flex-grow relative h-96">
                {isLoading ? (
                    <div className="flex justify-center items-center h-full">
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xl" />
                        <span className="ml-2 text-lg">Loading...</span>
                    </div>
                ) : (
                    <Pie data={pieData} options={options} />
                )}
            </div>
            {renderCarrierTable()}
        </div>
    );
};

export default ShipmentsByCarrierPieChart;
